import { GridRow } from 'ui/indexPages/tables/grid';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Tooltip } from 'ui/tooltip';
import { DataFeedsModals, DataFeedsModalStateType, useModalContext } from 'utils/contexts/modal';
import { localizeRelativeTime } from 'utils/i18nHelpers';
import { Link } from 'ui/links';
import { RedText, TableCell } from './Styled';
import { DataFeed, TABLE_COLUMN_ID } from '../../types';
import { useTableContext } from '../../contexts/Table';
import { useTranslations } from '../../hooks/useTranslations';
import { ActionButtonCombo } from './ActionButtonCombo';

type Props = {
  rowData: DataFeed;
};

export const testIds = {
  cell: 'df-table-cell',
  row: 'df-table-row',
};

const Cell = (field: TABLE_COLUMN_ID, rowData: DataFeed) => {
  const { t } = useTranslations();
  const { showModal } = useModalContext<DataFeedsModalStateType>();
  const { label, tooltip } = localizeRelativeTime(new Date(rowData.createdAt).toString());
  const { rateLimitPerMin } = rowData;

  switch (field) {
    case TABLE_COLUMN_ID.FEED_URL:
      return (
        <TableCell data-test={`${testIds.cell}-${field}`} key={`cell-${rowData.id}-${field}`}>
          <Link
            useReactRouter
            href={`/settings/dataFeeds/preview/${rowData.id}`}
            target="_blank"
            rel="noreferrer"
          >
            {rowData[field]}
          </Link>
        </TableCell>
      );
    case TABLE_COLUMN_ID.CREATED:
      return (
        <TableCell data-test={`${testIds.cell}-${field}`} key={`cell-${rowData.id}-${field}`}>
          <Tooltip tooltipContent={tooltip}>{label}</Tooltip>
        </TableCell>
      );
    case TABLE_COLUMN_ID.RATE_LIMIT:
      return (
        <TableCell data-test={`${testIds.cell}-${field}`} key={`cell-${rowData.id}-${field}`}>
          {rateLimitPerMin
            ? t('value.rateLimit', { ratePerMin: rateLimitPerMin.toLocaleString() })
            : t('value.rateLimitNone')}
        </TableCell>
      );
    case TABLE_COLUMN_ID.BUTTON:
      return (
        <TableCell data-test={`${testIds.cell}-${field}`} key={`cell-${rowData.id}-${field}`}>
          <ActionButtonCombo
            actions={[
              {
                content: t('menu.editAlias'),
                id: 'df-combo-btn-action-edit-alias',
                onClick: () => showModal({ modal: DataFeedsModals.EditAlias, dataFeed: rowData }),
              },
              {
                content: <RedText>{t('menu.deleteFeed')}</RedText>,
                id: 'df-combo-btn-action-delete',
                onClick: () =>
                  showModal({ modal: DataFeedsModals.ConfirmDelete, dataFeed: rowData }),
              },
            ]}
            label={t('button.editFeed')}
            onClick={() => showModal({ modal: DataFeedsModals.EditFeed, dataFeed: rowData })}
            size={BUTTON_SIZES.MEDIUM}
            variant={BUTTON_VARIANTS.SECONDARY}
          />
        </TableCell>
      );
    default:
      // ID, FEED_NAME, ALIAS, FORMAT
      return (
        <TableCell data-test={`${testIds.cell}-${field}`} key={`cell-${rowData.id}-${field}`}>
          {rowData[field]}
        </TableCell>
      );
  }
};

export const TableRow = ({ rowData }: Props) => {
  const { columns, gridLayout } = useTableContext();

  return (
    <GridRow data-test={testIds.row} gridLayout={gridLayout} key={`${testIds.row}-${rowData.id}`}>
      {columns.map((col) => Cell(col.id as TABLE_COLUMN_ID, rowData))}
    </GridRow>
  );
};
